'use client';

import { useEffect, useState } from 'react';
import { useOverlay } from '~/components';
import { Snackbar } from '@bgzt/ui';
import { useDeleteRemoveFavorite, usePutAddFavorite } from '~/queries/pms';
import { goToFavoritesOnApp, sendToProductFavorite } from '~/services/app-interface';
import { getFavoriteFolders } from '~/apis/pms';
import { showCollection } from '~/services/app-interface/show-collection';

const DEFAULT_BOTTOM_OFFSET = 12;

type FavoriteOptions = {
  /** 찜 추가/삭제시 보여주는 스낵바의 bottom offset (CTA 유무에 따라 달라져야 함) */
  snackbarBottomOffset?: number;
};

export const useFavorite = (defaultValue: boolean = false) => {
  const [isFavorite, setIsFavorite] = useState(defaultValue);
  const overlay = useOverlay();
  const { mutateAsync: addFavorite } = usePutAddFavorite();
  const { mutateAsync: removeFavorite } = useDeleteRemoveFavorite();

  useEffect(() => {
    setIsFavorite(defaultValue);
  }, [defaultValue]);

  const add = async (
    pid: number,
    /** 찜하기시 찜 컬렉션 바텀시트에서 보여줄 이미지 */
    image: string,
    { snackbarBottomOffset = DEFAULT_BOTTOM_OFFSET }: FavoriteOptions = {},
  ) => {
    setIsFavorite(true);

    await addFavorite(
      { pid },
      {
        onSuccess: async () => {
          // TODO: toggleFavoriteOnApp 찜 JSI 테스트후 삭제
          // toggleFavoriteOnApp({ pid, type: 'add' }).go();
          sendToProductFavorite(pid.toString(), 'add');
          // Todo: 버전업 필요 v2 -> v3 (api 대기)
          const { data } = await getFavoriteFolders({});
          const collectionExists = data?.length > 0;

          overlay.open(
            ({ isOpen, close }) => (
              <Snackbar
                isOpen={isOpen}
                onClose={() => {
                  close();
                }}
                sideSlot={collectionExists ? '컬렉션에 넣기' : '찜으로 이동'}
                onSideSlotClick={() => {
                  // case 1: 컬렉션에 넣기
                  if (collectionExists) {
                    showCollection({ pid: String(pid), image });
                    return;
                  }

                  // case 2: 찜으로 이동
                  goToFavoritesOnApp().go();
                }}
                bottomOffsetInfo={{ defaultOffset: snackbarBottomOffset }}
              >
                찜 완료!
              </Snackbar>
            ),
            { allowsPTR: true },
          );
        },
        onError: () => {
          setIsFavorite(false);
        },
      },
    );
  };

  const remove = async (pid: number) => {
    setIsFavorite(false);

    await removeFavorite(
      { pids: [pid] },
      {
        onSuccess: async () => {
          sendToProductFavorite(pid.toString(), 'delete');
        },
      },
    );
  };

  const toggle = (
    pid: number,
    image: string = '',
    { snackbarBottomOffset = DEFAULT_BOTTOM_OFFSET }: FavoriteOptions = {},
  ) => {
    !isFavorite ? add(pid, image, { snackbarBottomOffset }) : remove(pid);
  };

  return { add, remove, toggle, isFavorite };
};
